/**
 * @file Contributor UI — BackgroundFieldRenderer.
 * Background Field Renderer Component
 * Handles rendering of different field types
 */

import React, { useEffect, useState } from "react";
import { Checkbox } from "@mui/material";
import { FormField, DropdownOption } from "../../../../../../interfaces/formInterfaces";
import axiosClient from "../../../../../../api/axiosClient";
import { API_URLS } from "../../../../../../constants/apiUrls";
import type { SpecificityOption } from "../hooks/useBackgroundData";
import {
  getContributorNumericHtmlPropsFromCms,
  getContributorTextInputHtmlProps,
} from "../../../../../../utils/contributorFormFieldInputProps";
import { resolveUploadAssetUrl } from "../../../../../../utils/resolveUploadAssetUrl";
import { formatUnitSymbol } from "../../../../../../utils/formatUnitSymbol";
import BackgroundFieldLabel from "./BackgroundFieldLabel";
import {
  dataTypeLabelForValue,
  normalizeDataTypeValue,
} from "../../../../../../components/submission/backgroundDataType";

/** Type definition — Material. */
export interface Material {
  id: string;
  name: string;
  code: string;
  subMaterials: { id: string; name: string }[];
}

/** Type definition — Unit Option. */
export interface UnitOption {
  id: number;
  propertyId: number;
  unitSymbol: string;
  displayName?: string;
  isBase?: boolean;
}

interface BackgroundFieldRendererProps {
  field: FormField;
  values: Record<string, string | File>;
  materials: Material[];
  subMaterials: { id: string; name: string }[];
  rawMaterials: { id: string; name: string }[];
  allUnits: UnitOption[];
  enabledPairs: Record<string, boolean>;
  pairedGroups: Record<string, string>;
  /** Technological specificity options for the selected material (Manufacturing process / technology). */
  technologyOptions?: SpecificityOption[];
  /** Manufacturer specificity options for the selected material. */
  manufacturerOptions?: SpecificityOption[];
  disabled?: boolean;
  /** When set, overrides CMS `field.isMandatory` for the label asterisk. */
  showMandatory?: boolean;
  onMaterialSelect: (materialId: string) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onFileChange: (fieldName: string, file: File) => void;
  onCheckboxChange: (fieldName: string, checked: boolean) => void;
}

const UnitSelect: React.FC<{
  field: FormField;
  allUnits: UnitOption[];
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  disabled: boolean;
}> = ({ field, allUnits, value, onChange, disabled }) => {
  const [fetchedUnits, setFetchedUnits] = useState<UnitOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUnits = async () => {
      // Legacy: only use the flat all-units list when this field has no unitPropertyId.
      // When unitPropertyId is set (e.g. subMaterialUnit + 11), restrict to that property.
      const useAllUnitsUnscoped =
        ["subMaterialUnit", "rawMaterialUnit_01"].includes(field.name) &&
        !field.unitPropertyId;

      if (!field.unitPropertyId && !useAllUnitsUnscoped) {
        setFetchedUnits([]);
        return;
      }

      let unitOptions: UnitOption[] = [];
      if (useAllUnitsUnscoped) {
        unitOptions = allUnits;
      } else if (field.unitPropertyId) {
        unitOptions = allUnits.filter(
          (u) => Number(u.propertyId) === Number(field.unitPropertyId)
        );
      }

      // If we have units from allUnits, use them
      if (unitOptions.length > 0) {
        setFetchedUnits(unitOptions);
        return;
      }

      // Otherwise, fetch from API
      setLoading(true);
      try {
        if (!field.unitPropertyId) {
          setFetchedUnits([]);
          return;
        }
        const res = await axiosClient.get(API_URLS.UNITS.BY_PROPERTY(field.unitPropertyId));
        if (res.data?.success && res.data?.data) {
          const units = Array.isArray(res.data.data) ? res.data.data : [];
          const normalizedUnits = units.map((u: any) => ({
            id: u.id,
            propertyId: field.unitPropertyId!,
            unitSymbol: u.symbol || u.name || u.unitSymbol || "",
            displayName: u.displayName || u.name,
            isBase: u.isBase,
          }));
          setFetchedUnits(normalizedUnits);
        }
      } catch (err) {
        console.warn(`Failed to fetch units for property ${field.unitPropertyId}:`, err);
        setFetchedUnits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUnits();
  }, [field.unitPropertyId, field.name, allUnits]);

  const unitOptions = fetchedUnits.length > 0 ? fetchedUnits : [];

  return (
    <select
      name={field.name}
      value={value}
      onChange={onChange}
      disabled={disabled || loading || unitOptions.length === 0}
    >
      <option value="">Select Unit...</option>
      {unitOptions.map((u) => (
        <option key={u.id} value={u.id}>
          {formatUnitSymbol(u.unitSymbol)}
          {u.displayName ? ` (${u.displayName})` : ""}
        </option>
      ))}
    </select>
  );
};

const BackgroundFieldRenderer: React.FC<BackgroundFieldRendererProps> = ({
  field,
  values,
  materials,
  subMaterials,
  rawMaterials,
  allUnits,
  enabledPairs,
  pairedGroups,
  technologyOptions = [],
  manufacturerOptions = [],
  disabled: externalDisabled = false,
  showMandatory,
  onMaterialSelect,
  onChange,
  onFileChange,
  onCheckboxChange,
}) => {
  // Some backend forms mark unit fields as non-editable even though they must be selectable.
  // Force-enable these specific fields.
  const forceEditableByName = field.name === "subMaterialUnit";
  const isReadOnly =
    (field.type === "READONLY" || field.isEditable === false) && !forceEditableByName;
  let disabled = externalDisabled || isReadOnly;

  // Disable all paired fields when their group is not enabled
  // Only apply this to fields that are actually part of a paired group
  // Don't override externalDisabled or isReadOnly - those take precedence
  if (!disabled) {
    for (const [mainField, unitField] of Object.entries(pairedGroups)) {
      if (field.name === mainField || field.name === unitField) {
        // Only disable if the main field is not enabled
        if (!enabledPairs[mainField]) {
          disabled = true;
          break; // Exit early once we find a match
        }
        // If enabledPairs[mainField] is true, keep disabled as false (already set above)
      }
    }
  }

  // Defensive: CMS may send wrong types for known fields.
  const effectiveType =
    field.name === "subMaterialUnit"
      ? "UNIT"
      : field.name === "dataType"
        ? "DROPDOWN"
        : field.type;

  // Material-scoped specificity dropdowns (same APIs as Material Analysis), regardless of CMS field type.
  if (field.name === "technologyId" || field.name === "manufacturerId") {
    const list =
      field.name === "technologyId" ? technologyOptions : manufacturerOptions;
    const val = String(values[field.name] ?? "").trim();
    const materialChosen = Boolean(String(values.materialId ?? "").trim());

    if (isReadOnly) {
      const label =
        list.find((o) => o.id === val)?.name || val || "";
      return (
        <input
          type="text"
          name={field.name}
          value={label}
          readOnly
          className="readonly-input"
        />
      );
    }

    const noOptions = list.length === 0;
    const valueInList = list.some((o) => o.id === val);
    const showOrphanValue = Boolean(val && !valueInList);

    return (
      <select
        name={field.name}
        value={val}
        onChange={onChange}
        disabled={
          disabled ||
          !materialChosen ||
          (noOptions && !val)
        }
        title={
          !materialChosen
            ? "Select a material first"
            : noOptions && !val
              ? "No entries for this material"
              : undefined
        }
      >
        <option value="">
          {!materialChosen
            ? "Select material first…"
            : noOptions && !val
              ? "No options for this material"
              : "Select…"}
        </option>
        {showOrphanValue ? (
          <option value={val}>{`Current selection (id ${val})`}</option>
        ) : null}
        {list.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.name}
          </option>
        ))}
      </select>
    );
  }

  switch (effectiveType) {
    case "TEXT":
    case "EMAIL":
      // Handle file input type first
      if (field.inputType === "file" || field.name === "publicationFileUrl") {
        // For READONLY, show the URL if available
        if (isReadOnly) {
          const fileUrl = values[field.name] as string;
          return (
            <div className="file-field-readonly">
              {fileUrl ? (
                <a
                  href={resolveUploadAssetUrl(fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-link"
                >
                  {fileUrl}
                </a>
              ) : (
                <span className="no-file">No file uploaded</span>
              )}
            </div>
          );
        }

        return (
          <div className="file-upload-wrapper">
            <input
              type="file"
              name={field.name}
              accept="image/*,application/pdf,.pdf,.doc,.docx"
              disabled={disabled}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onFileChange(field.name, file);
                }
              }}
            />
            {values[`${field.name}_preview`] && (
              <div className="file-preview-container">
                {values[`${field.name}_preview`]?.toString().startsWith("data:image") ? (
                  <img
                    src={values[`${field.name}_preview`] as string}
                    alt="Preview"
                    className="file-preview"
                  />
                ) : (
                  <div className="file-preview-info">
                    <span>File selected: {(values[field.name] as File)?.name}</span>
                  </div>
                )}
              </div>
            )}
            {values[field.name] && typeof values[field.name] === "string" && !values[`${field.name}_preview`] && (
              <div className="file-url-display">
                <a
                  href={resolveUploadAssetUrl(values[field.name] as string)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-link"
                >
                  View uploaded file
                </a>
              </div>
            )}
          </div>
        );
      }

      // Check if this is a numeric field (either inputType="number" or field name suggests it's numeric)
      const numericFieldNames = [
        "density", "uValue", "compressiveStrength", "tensileStrength",
        "temperature", "diameter", "coverage", "thickness", "finishedQuantity"
      ];
      const isNumericField =
        field.inputType === "number" ||
        field.inputType === "double" ||
        numericFieldNames.includes(field.name) ||
        field.name.endsWith("_unit") ||
        field.name.endsWith("Unit");

      if (isNumericField) {
        const cmsNum = getContributorNumericHtmlPropsFromCms(field);
        // Only apply 0-100 restriction for percentage fields (like recycledContent)
        // Quantity fields (like finishedQuantity) should allow larger values
        const isPercentageField = field.name === "recycledContent";
        const minProp = cmsNum.min !== undefined ? cmsNum.min : 0;
        const maxProp =
          cmsNum.max !== undefined
            ? cmsNum.max
            : isPercentageField
              ? 100
              : undefined;
        const stepProp = cmsNum.step !== undefined ? cmsNum.step : 1;

        // Handle READONLY number fields
        if (isReadOnly) {
          return (
            <input
              type="number"
              name={field.name}
              value={(values[field.name] as string) || ""}
              placeholder={field.placeholder || ""}
              readOnly
              className="readonly-input"
              min={minProp}
              max={maxProp}
              step={stepProp}
            />
          );
        }

        return (
          <input
            type="number"
            name={field.name}
            value={(values[field.name] as string) || ""}
            onKeyDown={(e) => {
              // Prevent "e", "E", "+" from being entered (scientific notation)
              if (e.key === "e" || e.key === "E" || e.key === "+") {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              let val = e.target.value;
              // Remove "e", "E", "+" if somehow they get in
              val = val.replace(/[eE+]/g, "");
              const lo = Number(minProp);
              const hi =
                maxProp !== undefined ? Number(maxProp) : undefined;
              if (val !== "") {
                const n = Number(val);
                if (Number.isFinite(n)) {
                  if (Number.isFinite(lo) && n < lo) val = String(lo);
                  else if (
                    hi !== undefined &&
                    Number.isFinite(hi) &&
                    n > hi
                  ) {
                    val = String(hi);
                  }
                }
              }
              
              // Create a new event with the corrected value
              const syntheticEvent = {
                ...e,
                target: {
                  ...e.target,
                  name: field.name,
                  value: val,
                },
                currentTarget: {
                  ...e.currentTarget,
                  name: field.name,
                  value: val,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              
              onChange(syntheticEvent);
            }}
            min={minProp}
            max={maxProp}
            step={stepProp}
            placeholder={field.placeholder || ""}
            disabled={disabled}
          />
        );
      }

      // Handle READONLY text fields
      if (isReadOnly) {
        return (
          <input
            {...getContributorTextInputHtmlProps(field)}
            name={field.name}
            value={(values[field.name] as string) || ""}
            placeholder={field.placeholder || ""}
            readOnly
            className="readonly-input"
          />
        );
      }

      return (
        <input
          {...getContributorTextInputHtmlProps(field)}
          name={field.name}
          value={(values[field.name] as string) || ""}
          onChange={onChange}
          placeholder={field.placeholder || ""}
          disabled={disabled || field.name === "materialCode"}
          readOnly={field.name === "materialCode"}
        />
      );

    case "MATERIALS":
      return (
        <select
          name="materialId"
          value={(values.materialId as string) || ""}
          onChange={(e) => onMaterialSelect(e.target.value)}
          disabled={disabled}
        >
          <option value="">Select Material...</option>
          {materials.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      );

    case "RAW_MATERIALS":
      return (
        <select
          name={field.name}
          value={(values[field.name] as string) || ""}
          onChange={onChange}
          disabled={!values.materialId || disabled}
        >
          <option value="">Select Raw Material...</option>
          {rawMaterials.map((rm) => (
            <option key={rm.id} value={rm.id}>
              {rm.name}
            </option>
          ))}
        </select>
      );

    case "UNIT":
      return (
        <UnitSelect
          field={field}
          allUnits={allUnits}
          value={(values[field.name] as string) || ""}
          onChange={onChange}
          disabled={disabled || (field.name === "subMaterialUnit" && !values.subMaterialId)}
        />
      );

    case "SUBMATERIALS":
      return (
        <select
          name="subMaterialId"
          value={(values.subMaterialId as string) || ""}
          onChange={onChange}
          disabled={!values.materialId}
        >
          <option value="">Select Sub Material...</option>
          {subMaterials.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      );

    case "DROPDOWN": {
      const options: DropdownOption[] =
        field.dropdown?.options || field.dropdownValues || [];

      const rawValue = (values[field.name] as string) || "";
      const selectedValue =
        field.name === "dataType"
          ? normalizeDataTypeValue(rawValue, options)
          : rawValue;
      const valueInList = options.some(
        (opt) => String(opt.value ?? "") === selectedValue
      );
      const showOrphanValue = Boolean(selectedValue && !valueInList);
      const orphanLabel =
        field.name === "dataType"
          ? dataTypeLabelForValue(selectedValue, options)
          : selectedValue;

      // Handle READONLY dropdown
      if (isReadOnly) {
        const selectedOption = options.find(
          (opt) => String(opt.value ?? "") === selectedValue
        );
        return (
          <input
            type="text"
            value={
              selectedOption?.label ||
              (field.name === "dataType"
                ? dataTypeLabelForValue(rawValue, options)
                : rawValue) ||
              ""
            }
            readOnly
            className="readonly-input"
          />
        );
      }

      return (
        <select
          name={field.name}
          value={selectedValue}
          onChange={onChange}
          disabled={disabled}
        >
          <option value="">Select...</option>
          {showOrphanValue ? (
            <option value={selectedValue}>{orphanLabel}</option>
          ) : null}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    case "IMAGE":
      // Handle READONLY image fields
      if (isReadOnly) {
        const imageUrl = values[field.name] as string;
        return (
          <div className="image-field-readonly">
            {imageUrl ? (
              <img
                src={resolveUploadAssetUrl(imageUrl)}
                alt={field.label}
                className="readonly-image"
              />
            ) : (
              <span className="no-image">No image uploaded</span>
            )}
          </div>
        );
      }

      return (
        <div className="image-upload-wrapper">
          <input
            type="file"
            name={field.name}
            accept="image/*"
            disabled={disabled}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                onFileChange(field.name, file);
              }
            }}
          />
          {values[`${field.name}_preview`] && (
            <div className="image-preview-container">
              <img
                src={values[`${field.name}_preview`] as string}
                alt="Preview"
                className="image-preview"
              />
            </div>
          )}
          {values[field.name] && typeof values[field.name] === "string" && !values[`${field.name}_preview`] && (
            <div className="image-url-display">
              <img
                src={resolveUploadAssetUrl(values[field.name] as string)}
                alt="Uploaded"
                className="image-preview"
              />
            </div>
          )}
        </div>
      );

    case "CHECKBOX": {
      const checked =
        typeof values[field.name] === "string"
          ? values[field.name] === "true"
          : false;

      return (
        <div className="checkbox-field">
          <Checkbox
            checked={checked}
            onChange={(e) => onCheckboxChange(field.name, e.target.checked)}
            disabled={disabled}
          />
          <BackgroundFieldLabel
            label={field.label}
            isMandatory={showMandatory ?? field.isMandatory}
            infoText={field.infoText}
          />
        </div>
      );
    }

    case "RADIO": {
      const radioOptions: DropdownOption[] =
        field.dropdown?.options || field.dropdownValues || [];

      const selectedValue = (values[field.name] as string) || "";

      if (isReadOnly) {
        const selectedOption = radioOptions.find(
          (opt) => opt.value === selectedValue
        );
        return (
          <input
            type="text"
            value={selectedOption?.label || selectedValue || ""}
            readOnly
            className="readonly-input"
          />
        );
      }

      if (radioOptions.length === 0) {
        return (
          <input
            type="text"
            name={field.name}
            value={selectedValue}
            onChange={onChange}
            disabled={disabled}
            placeholder={field.placeholder || ""}
          />
        );
      }

      return (
        <div className="radio-group" role="radiogroup" aria-label={field.label}>
          {radioOptions.map((opt) => (
            <label key={opt.value} className="radio-option">
              <input
                type="radio"
                name={field.name}
                value={opt.value}
                checked={selectedValue === opt.value}
                onChange={onChange}
                disabled={disabled}
              />
              {opt.label}
            </label>
          ))}
        </div>
      );
    }

    case "FILE":
      // Handle READONLY file fields
      if (isReadOnly) {
        const fileValue = values[field.name];
        
        // If it's a File object, show file name
        if (fileValue instanceof File) {
          return (
            <div className="file-field-readonly">
              <div className="file-info-readonly">
                <span className="file-name-readonly">{fileValue.name}</span>
                <span className="file-size-readonly">
                  ({(fileValue.size / 1024).toFixed(2)} KB)
                </span>
              </div>
            </div>
          );
        }
        
        // If it's a URL string, show as link
        const fileUrl = fileValue as string;
        if (fileUrl) {
          // Extract filename from URL if possible
          const fileName = fileUrl.split('/').pop() || fileUrl;
          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
          const resolvedUrl = resolveUploadAssetUrl(fileUrl);
          
          return (
            <div className="file-field-readonly">
              {isImage ? (
                <div className="file-readonly-container">
                  <img
                    src={resolvedUrl}
                    alt={field.label}
                    className="readonly-file-image"
                    onError={(e) => {
                      // If image fails to load, show as link instead
                      e.currentTarget.style.display = 'none';
                      const link = e.currentTarget.nextElementSibling as HTMLElement;
                      if (link) link.style.display = 'block';
                    }}
                  />
                  <a
                    href={resolvedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="file-link"
                    style={{ display: 'none' }}
                  >
                    {fileName}
                  </a>
                </div>
              ) : (
                <a
                  href={resolvedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-link"
                >
                  📄 {fileName}
                </a>
              )}
            </div>
          );
        }
        
        // No file
        return (
          <div className="file-field-readonly">
            <span className="no-file">No file uploaded</span>
          </div>
        );
      }

      return (
        <div className="file-upload-wrapper">
          <input
            type="file"
            name={field.name}
            accept={field.placeholder || "image/*,application/pdf,.pdf,.doc,.docx,.txt,.csv"}
            disabled={disabled}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                onFileChange(field.name, file);
              }
            }}
          />
          {values[`${field.name}_preview`] && (
            <div className="file-preview-container">
              {values[`${field.name}_preview`]?.toString().startsWith("data:image") ? (
                <img
                  src={values[`${field.name}_preview`] as string}
                  alt="Preview"
                  className="file-preview"
                />
              ) : (
                <div className="file-preview-info">
                  <span>File selected: {(values[field.name] as File)?.name}</span>
                  {values[field.name] instanceof File && (
                    <span className="file-size">
                      ({(values[field.name] as File).size / 1024} KB)
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          {values[field.name] && typeof values[field.name] === "string" && !values[`${field.name}_preview`] && (
            <div className="file-url-display">
              <a
                href={resolveUploadAssetUrl(values[field.name] as string)}
                target="_blank"
                rel="noopener noreferrer"
                className="file-link"
              >
                View uploaded file
              </a>
            </div>
          )}
          {values[field.name] instanceof File && !values[`${field.name}_preview`] && (
            <div className="file-info">
              <span>File: {(values[field.name] as File).name}</span>
              <span className="file-size">
                ({(values[field.name] as File).size / 1024} KB)
              </span>
            </div>
          )}
        </div>
      );

    case "READONLY":
      // Handle READONLY fields - render based on their inputType or field properties
      const readonlyValue = (values[field.name] as string) || "";
      
      // If it has a dropdown, show the selected option label
      if (field.dropdown?.options || field.dropdownValues) {
        const options = field.dropdown?.options || field.dropdownValues || [];
        const selectedOption = options.find((opt: any) => opt.value === readonlyValue);
        return (
          <input
            type="text"
            value={selectedOption?.label || readonlyValue || ""}
            readOnly
            className="readonly-input"
            placeholder={field.placeholder || ""}
          />
        );
      }
      
      // If it's a number field
      if (field.inputType === "number" || field.inputType === "double" ||
          ["density", "uValue", "compressiveStrength", "tensileStrength", 
           "temperature", "diameter", "coverage", "thickness", "finishedQuantity"].includes(field.name)) {
        const cmsNum = getContributorNumericHtmlPropsFromCms(field);
        return (
          <input
            type="number"
            name={field.name}
            value={readonlyValue}
            placeholder={field.placeholder || ""}
            readOnly
            className="readonly-input"
            min={cmsNum.min}
            max={cmsNum.max}
            step={cmsNum.step}
          />
        );
      }
      
      // Default: text input
      return (
        <input
          {...getContributorTextInputHtmlProps(field)}
          name={field.name}
          value={readonlyValue}
          placeholder={field.placeholder || ""}
          readOnly
          className="readonly-input"
        />
      );

    default:
      return null;
  }
};

/** React component — Background Field Renderer. */
export default BackgroundFieldRenderer;

